import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  addDays,
  andFilters,
  civilToday,
  exportQueryOf,
  formatCivilDate,
  links,
  resolveFormattingLocale,
  useListSearch,
  type FilterField,
} from '@eis/core';
import {
  EffectiveDateSelector,
  ExportButton,
  InfiniteDataGrid,
  PageHeader,
  PageLayout,
  PropertyFilter,
  StatusChip,
  cardSurfaceSx,
  catalogLabel,
  useGridExportColumns,
  type ExportableGridColDef,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { useFilterCatalog, useInfiniteList, usePolicyFilterValues } from '../api/policies';
import { dataValueOf, outOfSliceHit, toRows, type PolicyRow } from '../model/types';

/** How many of the product's catalog fields join the system columns. */
const LEADING_PRODUCT_FIELDS = 3;

/**
 * Policen — the operations persona's list (SDD-017 §9).
 *
 * The order of the controls is the order of the question: WHICH product (the tarif selector — it
 * decides which product fields the property filter and the grid offer, `:filterCatalog?tarifSlug=`),
 * AS OF WHEN ("Stand am", the one effective-date picker, CLAUDE.md rule 6; default today), and
 * THEN what to narrow by. Everything is in the URL (`?tarif=&validAt=&filters=&search=&order=`),
 * so a filtered list is a link; the rows come page by page as the reader scrolls, ordered and
 * filtered by the service.
 *
 * A search can match a policy in a slice OTHER than the one the row shows (PPL-878): the plate
 * 123ABC was the policy's until June. The row then carries a chip naming that slice, linking to
 * the policy at it with the matched field highlighted.
 *
 * No version vocabulary appears anywhere on this surface (SSOT I-6).
 */
export function PolicyListPage(): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const url = useListSearch();
  const tarif = typeof url.raw.tarif === 'string' ? url.raw.tarif : '';

  // The system catalog names the tenant's tarifs; the tarif's catalog adds its product fields.
  const systemCatalog = useFilterCatalog('policies');
  const catalog = useFilterCatalog('policies', tarif === '' ? {} : { tarifSlug: tarif });
  const loadValues = usePolicyFilterValues(tarif === '' ? undefined : tarif);
  const tarifs =
    systemCatalog.data?.fields?.find((field) => field.key === 'tarifSlug')?.values ?? [];

  const list = useInfiniteList('policies', {
    ...url.params,
    filters: andFilters(tarif === '' ? undefined : `tarifSlug==${tarif}`, url.params.filters),
  });
  const rows = useMemo(() => toRows(list.items), [list.items]);

  const productFields = useMemo<FilterField[]>(
    () =>
      (catalog.data?.fields ?? [])
        .filter((field) => field.group === 'PRODUCT' && !(field.key ?? '').includes('[]'))
        .slice(0, LEADING_PRODUCT_FIELDS),
    [catalog.data],
  );

  const searching = (url.params.search ?? '') !== '';

  // Each column names its path in the list item (`PolicyListEntry`), so the export writes what
  // the grid shows (SDD-020 §9); the match chip has no data of its own.
  const columns = useMemo<ExportableGridColDef<PolicyRow>[]>(() => {
    const product: ExportableGridColDef<PolicyRow>[] = productFields.map((field) => ({
      field: field.key ?? '',
      exportPath: `policy.${field.key ?? ''}`,
      headerName: catalogLabel(i18n, field, field.key ?? ''),
      width: 150,
      valueGetter: (_value, row) => {
        const value = dataValueOf(row.data, field.key ?? '');
        if (value === undefined) return '—';
        const known = field.values?.find((entry) => entry.value === String(value));
        return known === undefined ? String(value) : catalogLabel(i18n, known, String(value));
      },
    }));
    return [
      { field: 'code', headerName: t('policies.list.code'), width: 150, exportPath: 'policy.code' },
      // While searching, WHY a row is here comes right after which row it is — a hit in an
      // earlier slice must not hide behind a horizontal scroll.
      ...(searching
        ? [
            {
              field: 'match',
              headerName: t('policies.list.match'),
              width: 460,
              sortable: false,
              renderCell: ({ row }: { row: PolicyRow }): ReactNode => (
                <MatchChip row={row} locale={locale} />
              ),
            } satisfies ExportableGridColDef<PolicyRow>,
          ]
        : []),
      {
        field: 'status',
        headerName: t('policies.list.status'),
        width: 130,
        exportPath: 'statusName',
        renderCell: ({ row }) => (
          <StatusChip status={row.status} label={t(`policies.status.${row.status}` as never)} />
        ),
      },
      {
        field: 'policyholderLabel',
        headerName: t('policies.list.policyholder'),
        flex: 1,
        minWidth: 220,
        exportPath: 'partners.policyholder.name',
        /**
         * The one column on this screen that carries a claim about the MODEL: an inherited
         * policyholder belongs to the Gruppen-Police and is resolved at read (decision D7).
         */
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
            <span>{row.policyholderLabel}</span>
            {row.policyholderInherited && (
              <Tooltip title={t('policies.list.inheritedHint')}>
                <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center' }}>
                  <LinkIcon fontSize="inherit" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {t('policies.list.inherited')}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Stack>
        ),
      },
      {
        field: 'objectIdentifier',
        headerName: t('policies.list.insuredObject'),
        flex: 1,
        minWidth: 220,
        sortable: false,
        // The service's read-time projection of the insured object (SDD-022 D-4).
        exportPath: 'objectIdentifier',
        valueFormatter: (value: string) => (value === '' ? '—' : value),
      },
      {
        field: 'insuredLabel',
        headerName: t('policies.list.insured'),
        flex: 1,
        minWidth: 160,
        // Names by role, resolved by the service as this grid resolves them (SDD-020 §14).
        exportPath: 'partners.insured_person.name',
      },
      {
        field: 'validFrom',
        headerName: t('policies.field.validFrom'),
        width: 120,
        exportPath: 'snapshot.validFrom',
        exportType: 'DATE',
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
      {
        field: 'monthlyPremium',
        headerName: t('policies.list.premium'),
        width: 130,
        exportPath: 'policy.premium.monthly',
        exportType: 'MONEY',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) =>
          row.monthlyPremium === '' ? '—' : `${row.monthlyPremium} ${row.currency}`,
      },
      ...product,
    ];
  }, [t, i18n, locale, productFields, searching]);

  const sortKeys = useMemo<Record<string, string>>(
    () => ({
      code: 'code',
      status: 'status',
      policyholderLabel: 'holderName',
      validFrom: 'validFrom',
      monthlyPremium: 'premiumAmount',
      ...Object.fromEntries(
        productFields
          .filter((field) => field.sortable === true)
          .map((field) => [field.key ?? '', field.key ?? '']),
      ),
    }),
    [productFields],
  );

  const validAt = url.search.validAt ?? civilToday();
  const exportColumns = useGridExportColumns(columns);
  const listFilters = andFilters(
    tarif === '' ? undefined : `tarifSlug==${tarif}`,
    url.params.filters,
  );

  return (
    <PageLayout
      header={
        <PageHeader
          title={t('policies.title')}
          description={t('policies.subtitle')}
          actions={
            <ExportButton
              service="insurance"
              permission={p.exports.create}
              rowCount={list.isLoading ? undefined : rows.length}
              buildRequest={(format) => ({
                collection: 'policies',
                query: exportQueryOf(
                  { filters: listFilters, search: url.params.search, order: url.params.order },
                  { validAt: list.cut?.validAt ?? validAt, asOf: list.cut?.asOf },
                ),
                columns: exportColumns,
                format,
                label: t('policies.title'),
              })}
            />
          }
        />
      }
    >
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: 'wrap', alignItems: 'flex-start' }}
          >
            <TextField
              select
              size="small"
              label={t('policies.list.tarif')}
              value={tarif}
              onChange={(event) => {
                // Another product's fields mean nothing here: its filters and order go with it.
                url.set({ tarif: event.target.value, filters: '', order: '' });
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="">{t('policies.list.tarifAll')}</MenuItem>
              {tarifs.map((entry) => (
                <MenuItem key={entry.value} value={entry.value ?? ''}>
                  {catalogLabel(i18n, entry, entry.value ?? '')}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ width: 200 }}>
              <EffectiveDateSelector
                label={t('policies.list.validAt')}
                value={validAt}
                onChange={(next) => {
                  url.set({ validAt: next === null || next === civilToday() ? '' : next });
                }}
                locale={locale}
              />
            </Box>
          </Stack>
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search: url.params.search ?? '' }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
            loadValues={loadValues}
          />
          <InfiniteDataGrid
            rows={rows}
            list={list}
            columns={columns}
            ariaLabel={t('policies.list.ariaLabel')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={sortKeys}
            showCut
            errorDescription={t('policies.error.list')}
            emptyDescription={t('policies.list.empty')}
            onRowClick={(row) => {
              if (row.code === '') return;
              void navigate({
                href: links.insurance.policy(row.code, { validAt: url.params.validAt }),
              });
            }}
          />
        </Stack>
      </Paper>
    </PageLayout>
  );
}

/**
 * "Treffer in Stand 15.01.–30.06.2026: Versicherungskennzeichen 123ABC" — the slice the search
 * matched when it is not the row's, linking to the policy AT that slice with the field highlighted.
 */
function MatchChip({
  row,
  locale,
}: {
  readonly row: PolicyRow;
  readonly locale: string;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const navigate = useNavigate();
  const hit = outOfSliceHit(row.match, (validTo) => addDays(validTo, -1));
  if (hit === undefined || row.match === undefined) return null;
  const from = formatCivilDate(hit.validFrom, locale, { day: '2-digit', month: '2-digit' });
  const range =
    hit.lastDay === undefined
      ? t('policies.list.matchFrom', { from: formatCivilDate(hit.validFrom, locale) })
      : `${from}–${formatCivilDate(hit.lastDay, locale)}`;
  const label = t('policies.list.matchOtherSlice', {
    range,
    field: catalogLabel(i18n, row.match, hit.field),
    value: hit.value,
  });
  const href = links.insurance.policy(row.code, { validAt: hit.validFrom, hl: hit.field });
  return (
    <Chip
      size="small"
      variant="outlined"
      color="info"
      component="a"
      href={href}
      clickable
      label={label}
      onClick={(event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        void navigate({ href });
      }}
    />
  );
}
